import BaseModal from 'baseUI/Modal/BaseModal';
import { useContextCreateNewOptionModal } from './CreateNewOptionModalContext';
import useViewModel, { FormValues, createNewOptionModalId } from './viewModel';
import { css, cx, CSSObject } from 'core/emotion';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import Label from '../../../baseUI/form/Label';
import FieldTextInput from '../../../baseUI/form/FieldTextInput';
import FieldNumberInput from '../../../baseUI/form/FieldNumberInput';
import FieldDropdown from '../../../baseUI/form/FieldDropdown';
import { Button, useTheme } from '@nuvo-importer/common';
import DataModelSheetMatcher from '../../../matching/DataModelSheetMatcher';
import DataModelSheetMatching from '../../../matching/DataModelSheetMatching';
import { useMemo } from 'react';

type CreateNewOptionModalProps = {
  dataModelSheetMatcher: DataModelSheetMatcher;
  setDataModelSheetMatcher: (
    dataModelSheetMatcher: DataModelSheetMatcher
  ) => void;
  setDataModelSheetMatching: (
    dataModelSheetMatching: DataModelSheetMatching
  ) => void;
};

const CreateNewOptionModal = ({
  dataModelSheetMatcher,
  setDataModelSheetMatcher,
  setDataModelSheetMatching,
}: CreateNewOptionModalProps) => {
  const theme = useTheme();
  const { isOpen, close } = useContextCreateNewOptionModal();
  const {
    onSubmit,
    dropdownOptions,
    modalElement,
    validate,
    initialValues,
    hasSubmit,
    inputRef,
    handleDropdownChange,
  } = useViewModel({
    isOpen,
    dataModelSheetMatcher,
    setDataModelSheetMatcher,
    setDataModelSheetMatching,
  });
  const { t } = useTranslation();

  const configureStyled = useMemo(() => {
    return theme.getColumnMatchTheme().dialogCreateCustomOption;
  }, [theme]);

  return (
    <BaseModal
      gravity="middle"
      className="w-full !p-0 sm:max-w-xl"
      isOpen={isOpen}
      onCloseModal={() => {
        close();
      }}
      wrapperClassName={cx(css`
        z-index: 10000 !important;
      `)}
      isShowCloseIcon={true}
      id={createNewOptionModalId}
      overlayStyled={configureStyled?.overlay}
      bodyStyled={configureStyled?.root}
      closeIconStyled={configureStyled?.closeIcon}
    >
      <Form<FormValues>
        onSubmit={onSubmit}
        validate={(values) => {
          if (hasSubmit) {
            return true;
          }
          return validate({
            ...values,
            optionName: values.optionName?.trim() ?? '',
          });
        }}
        initialValues={initialValues as FormValues}
        render={({ handleSubmit, values, form }) => {
          return (
            <form className="p-6 pt-0 text-left" onSubmit={handleSubmit}>
              <div>
                <p
                  className={cx(
                    'text-neutral-gray text-2xl font-semibold',
                    css({ '&&': configureStyled?.title })
                  )}
                >
                  {t('txt_create_new_option')}
                </p>
                <p
                  className={cx(
                    'text-gray-970 mt-2 text-sm leading-5',
                    css({ '&&': configureStyled?.description })
                  )}
                >
                  {t('txt_create_new_option_description')}
                </p>
              </div>
              <div className="mt-6">
                <Label
                  description={t(
                    'txt_create_new_option_option_name_field_description'
                  )}
                  className="mb-1"
                  textClassName={css({
                    '&&': configureStyled?.optionName?.label,
                  })}
                  iconClassName={css({
                    '&&': {
                      color: (configureStyled?.optionName?.icon as CSSObject)
                        ?.color,
                    },
                  })}
                >
                  {t('txt_option_name')}
                </Label>
                {values.optionType === 'string' ? (
                  <FieldTextInput
                    ref={inputRef}
                    className={css({
                      '&&': configureStyled?.optionName?.input,
                    })}
                    errorMessageClassName={css({
                      '&&': configureStyled?.optionName?.errorMessage,
                    })}
                    name="optionName"
                    placeholder={t(
                      'txt_create_new_option_option_name_field_placeholder'
                    )}
                  />
                ) : (
                  <FieldNumberInput
                    ref={inputRef}
                    withDecimal={values.optionType === 'float'}
                    className={css({
                      '&&': configureStyled?.optionName?.input,
                    })}
                    errorMessageClassName={css({
                      '&&': configureStyled?.optionName?.errorMessage,
                    })}
                    name="optionName"
                    placeholder={t(
                      'txt_create_new_option_option_name_field_placeholder'
                    )}
                    hint={
                      values.optionType === 'float'
                        ? t('txt_message_float_only_numbers')
                        : t('txt_message_int_only_numbers')
                    }
                  />
                )}
              </div>
              <div className="mt-4">
                <Label
                  description={t(
                    'txt_create_new_option_option_type_field_description'
                  )}
                  textClassName={css({
                    '&&': configureStyled?.optionType?.label,
                  })}
                  iconClassName={css({
                    '&&': {
                      color: (configureStyled?.optionType?.icon as CSSObject)
                        ?.color,
                    },
                  })}
                  className="mb-1"
                >
                  {t('txt_option_type')}
                </Label>
                <FieldDropdown
                  name="optionType"
                  placeholder={t('txt_select_option_type')}
                  options={dropdownOptions}
                  rootDropdownPortal={modalElement ?? undefined}
                  dropdownStyled={configureStyled?.optionType?.dropdown}
                  onChange={handleDropdownChange(values, form.change)}
                />
              </div>
              <div className="flex justify-end pt-10">
                <Button
                  variant="secondary"
                  className="mr-4"
                  onClick={() => {
                    close();
                  }}
                >
                  {t('txt_cancel')}
                </Button>
                <Button type="submit">{t('txt_create')}</Button>
              </div>
            </form>
          );
        }}
      />
    </BaseModal>
  );
};

export default CreateNewOptionModal;
